import type { MeContext, WorkspaceNode } from '@vizit/api';
import { ACCESS_ROUTES, SwitcherEntityKind } from '../constants';
import type { AccessRouteParams, HierarchyTrailItem } from '../types';
import { findPortfolioById } from './find-portfolio-by-id';
import { findWorkspaceBySlug } from './find-workspace-by-slug';
import { getAccessiblePortfolios } from './get-accessible';
import { isHierarchical } from './is-hierarchical';
import { hasElevatedGrant } from './manage-users';
import { resolveOrganizationGridHref } from './route-guards';

type TrailLevel = Omit<HierarchyTrailItem, 'isCurrent' | 'isInteractive'>;

function workspaceLevel(workspace: WorkspaceNode): TrailLevel {
  return {
    id: workspace.id,
    name: workspace.name,
    kind: SwitcherEntityKind.Workspace,
    childrenHref: null,
  };
}

function applyNavigationPolicy(levels: TrailLevel[], canNavigate: boolean): HierarchyTrailItem[] {
  return levels.map((level, index) => {
    const childrenHref = canNavigate ? level.childrenHref : null;

    return {
      ...level,
      childrenHref,
      isInteractive: childrenHref !== null,
      isCurrent: index === levels.length - 1,
    };
  });
}

export function buildHierarchyTrail(context: MeContext, routeParams: AccessRouteParams = {}): HierarchyTrailItem[] {
  const canNavigate = hasElevatedGrant(context);
  const workspace = routeParams.workspaceSlug ? findWorkspaceBySlug(context, routeParams.workspaceSlug) : undefined;

  if (!isHierarchical(context.organization)) {
    return workspace ? applyNavigationPolicy([workspaceLevel(workspace)], canNavigate) : [];
  }

  const portfolios = getAccessiblePortfolios(context);
  const portfolioId = workspace?.portfolioId ?? routeParams.portfolioId;
  const portfolio = findPortfolioById(context, portfolioId ?? null);

  const levels: TrailLevel[] = [
    {
      id: context.organization.id,
      name: context.organization.name,
      kind: SwitcherEntityKind.Organization,
      childrenHref: portfolios.length > 1 ? resolveOrganizationGridHref(context) : null,
    },
  ];

  if (portfolio) {
    levels.push({
      id: portfolio.id,
      name: portfolio.name,
      kind: SwitcherEntityKind.Portfolio,
      childrenHref: portfolio.workspaces.length > 1 ? ACCESS_ROUTES.portfolioWorkspaces(portfolio.id) : null,
    });

    if (workspace) {
      levels.push(workspaceLevel(workspace));
    }
  }

  return applyNavigationPolicy(levels, canNavigate);
}
